import { FilterType } from 'components/entityViewer/entitiesDropDownFilter/entityFilterControls/FilterType';

/** @typedef {'OUTBOUND' | 'INBOUND'} FcTransferDirection */

/**
 * Direction tab values sent to the list API as a filter (not separate routes).
 * Default is outbound per tech spec §5.2.
 */
export const FC_TRANSFER_DIRECTION = Object.freeze({
    OUTBOUND: 'OUTBOUND',
    INBOUND: 'INBOUND',
});

export const DEFAULT_FC_TRANSFER_DIRECTION = FC_TRANSFER_DIRECTION.OUTBOUND;

export const FC_TRANSFER_API_FILTER_KEYS = Object.freeze({
    DIRECTION: 'direction',
    SOURCE_FACILITY_ID: 'sourceFacilityId',
    DESTINATION_FACILITY_ID: 'destinationFacilityId',
});

/**
 * Remote filter option query ids (wired to hooks in later tickets where needed).
 */
export const FILTER_QUERY_KEYS = Object.freeze({
    FACILITIES: 'facilities',
    STATUSES: 'statuses',
    CREATED_BY_USERS: 'createdByUsers',
});

/**
 * Tab-dependent label for the FC multi-select (tech spec §4, §5.1).
 * @param {FcTransferDirection} direction
 * @returns {string}
 */
export function getFcFilterLabel(direction) {
    return direction === FC_TRANSFER_DIRECTION.INBOUND ? 'Source FC' : 'Destination FC';
}

/**
 * FC filter — options populated in `FCTransfersFilters` (facility catalog).
 * Label is overridden per direction via `buildFCTransferFilterConfigsWithDirection`.
 */
export const fcIdsFilterConfig = {
    key: 'fcIds',
    label: 'Destination FC',
    type: FilterType.SELECT,
    enableSearch: true,
    multi: true,
    options: [],
    deletable: true,
    remoteOptions: {
        queryId: FILTER_QUERY_KEYS.FACILITIES,
    },
    menuProps: {
        placeholder: 'Search FCs...',
        mergeWithStaticOptions: false,
    },
};

export const transferNumberFilterConfig = {
    key: 'transferNumber',
    label: 'Transfer #',
    type: FilterType.TEXT,
    deletable: true,
    inputProps: {
        placeholder: 'Enter transfer number…',
    },
};

/** Single calendar day → URL/API (tech spec §5.1 `Created On`). */
export const createdOnFilterConfig = {
    key: 'createdOn',
    label: 'Created On',
    type: FilterType.DATE_RANGE,
    range: false,
    deletable: true,
};

/** Single calendar day; API query param `estimatedDepartureDate` (inventory v1 search). */
export const estimatedDepartureDateFilterConfig = {
    key: 'estimatedDepartureDate',
    label: 'Estimated departure',
    type: FilterType.DATE_RANGE,
    range: false,
    deletable: true,
};

export const statusesFilterConfig = {
    key: 'statuses',
    label: 'Status',
    type: FilterType.SELECT,
    enableSearch: true,
    multi: true,
    options: [],
    deletable: true,
    remoteOptions: {
        queryId: FILTER_QUERY_KEYS.STATUSES,
    },
    menuProps: {
        placeholder: 'Select statuses…',
        mergeWithStaticOptions: false,
    },
};

export const createdByUserIdsFilterConfig = {
    key: 'createdByUserIds',
    label: 'Created By',
    type: FilterType.SELECT,
    enableSearch: true,
    multi: true,
    options: [],
    deletable: true,
    remoteOptions: {
        queryId: FILTER_QUERY_KEYS.CREATED_BY_USERS,
    },
    menuProps: {
        placeholder: 'Search users…',
        mergeWithStaticOptions: false,
    },
};

/**
 * Display order for the FC Transfers filter dropdown (tech spec §5.1).
 */
export const FCTransferFilterConfigArray = [
    fcIdsFilterConfig,
    transferNumberFilterConfig,
    createdOnFilterConfig,
    estimatedDepartureDateFilterConfig,
    statusesFilterConfig,
    createdByUserIdsFilterConfig,
];

export const FCTransferFilterConfigMap = FCTransferFilterConfigArray.reduce((acc, config) => {
    acc[config.key] = config;
    return acc;
}, {});

/**
 * TanStack column ids for sortable columns (must match `FCTransfersTable` column `id`s).
 * `apiField` values are sent as `sortField` on `GET /inventory/v1/fc-transfers`.
 */
export const FCTransferSortConfig = {
    sortableFields: {
        id: { apiField: 'id' },
        createdAt: { apiField: 'createdAt' },
        estimatedDepartureDate: { apiField: 'estimatedDepartureDate' },
    },
};

/**
 * Schema for `fromUrl` / `toUrl` / `toApi` (shared `filterTransformers`).
 * List URL state may also include `direction` (OUTBOUND | INBOUND); it pass-throughs in `prepareForUrl` / `toApi`.
 *
 * @type {import('shared/utils/filterTransformers').FilterTransformConfig}
 */
export const FCTransferTransformConfig = {
    singleDateKeys: ['createdOn', 'estimatedDepartureDate'],
    numericArrayKeys: ['fcIds', 'createdByUserIds'],
    stringArrayKeys: ['statuses'],
};

/**
 * @param {FcTransferDirection | undefined} direction
 * @returns {FcTransferDirection}
 */
export function normalizeFCTransferDirection(direction) {
    if (direction === FC_TRANSFER_DIRECTION.INBOUND || direction === FC_TRANSFER_DIRECTION.OUTBOUND) {
        return direction;
    }
    return DEFAULT_FC_TRANSFER_DIRECTION;
}

/**
 * For outbound rows, the logged-in facility is the source.
 * For inbound rows, the logged-in facility is the destination.
 *
 * @param {FcTransferDirection | undefined} direction
 * @returns {'sourceFacilityId' | 'destinationFacilityId'}
 */
export function getCurrentFacilityApiKeyByDirection(direction) {
    return normalizeFCTransferDirection(direction) === FC_TRANSFER_DIRECTION.INBOUND
        ? FC_TRANSFER_API_FILTER_KEYS.DESTINATION_FACILITY_ID
        : FC_TRANSFER_API_FILTER_KEYS.SOURCE_FACILITY_ID;
}

/**
 * Builds required context for the FC Transfers list (merged into filter state and URL sync).
 *
 * - **`direction`:** UI / URL only (`OUTBOUND` | `INBOUND`). Not sent on `GET /inventory/v1/fc-transfers`;
 *   the HTTP API has no `direction` param.
 * - **Facility column:** Outbound tab → `sourceFacilityId` = logged-in facility (you are the source).
 *   Inbound tab → `destinationFacilityId` = logged-in facility (you are the destination). That is how
 *   “direction” is expressed to the backend.
 *
 * @param {Object} params
 * @param {FcTransferDirection | undefined} params.direction
 * @param {number | string | undefined | null} params.currentFacilityId
 * @returns {Object}
 */
export function buildRequiredFCTransferListFilters({ currentFacilityId, direction }) {
    const normalizedDirection = normalizeFCTransferDirection(direction);
    const facilityFilterKey = getCurrentFacilityApiKeyByDirection(normalizedDirection);

    const requiredFilters = {
        [FC_TRANSFER_API_FILTER_KEYS.DIRECTION]: normalizedDirection,
    };

    if (currentFacilityId !== undefined && currentFacilityId !== null && currentFacilityId !== '') {
        const normalizedFacilityId =
            typeof currentFacilityId === 'string' ? parseInt(currentFacilityId, 10) : currentFacilityId;
        if (typeof normalizedFacilityId === 'number' && Number.isNaN(normalizedFacilityId)) {
            return requiredFilters;
        }
        requiredFilters[facilityFilterKey] = normalizedFacilityId;
    }

    return requiredFilters;
}

/**
 * Applies required transfer context filters onto ad-hoc filter values.
 *
 * @param {Object} baseFilters
 * @param {Object} params
 * @param {FcTransferDirection | undefined} params.direction
 * @param {number | string | undefined | null} params.currentFacilityId
 * @returns {Object}
 */
export function mergeFCTransferListFiltersWithRequiredContext(baseFilters, { currentFacilityId, direction }) {
    return {
        ...baseFilters,
        ...buildRequiredFCTransferListFilters({ currentFacilityId, direction }),
    };
}

/**
 * @param {FcTransferDirection} direction
 * @returns {typeof FCTransferFilterConfigArray}
 */
export function buildFCTransferFilterConfigsWithDirection(direction) {
    const fcLabel = getFcFilterLabel(direction);
    return FCTransferFilterConfigArray.map((cfg) => (cfg.key === 'fcIds' ? { ...cfg, label: fcLabel } : cfg));
}
